<template>
    <main class="container relative pb-10 pt-25 md:pb-40 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <section class="mb-10 mt-10 md:mb-20">
            <AtomTitle level="1"> {{ data?.data.name }} </AtomTitle>
            <div
                data-cromi-recovery-summary
                v-html="data?.data.description"
                class="text- mt-5 text-accent-100 md:w-2/3"></div>

            <LazyApiSection
                recovery-required
                :recovery-ready="hasDeferredLoaded"
                min-height="32rem"
                @visible="loadDeferredSolutionsContent">
                <div v-if="hasDeferredLoaded">
                    <div
                        v-if="solutions.length"
                        class="mt-10 space-y-2">
                        <NuxtLink
                            v-for="card of solutions"
                            :key="card.slug"
                            :to="`/solutions/${card.slug}/`"
                            class="bg-noise group relative flex w-full items-center gap-3 overflow-hidden rounded-12 bg-white/5 transition-colors duration-200 hover:bg-white/20 focus-visible:bg-white/20 max-md:flex-col md:h-[18.75rem] lg:gap-10">
                            <picture
                                class="relative block h-full shrink-0 basis-[55%] overflow-hidden rounded-12 before:absolute before:inset-0 before:size-full before:rounded-12 before:bg-black/40 before:transition-colors before:duration-200 group-hover:before:bg-transparent lg:basis-[60%]">
                                <source
                                    :srcset="
                                        getMediaLink(
                                            card.cover?.[0]?.webp_url || '',
                                            1200
                                        ) || ''
                                    "
                                    type="image/webp" />

                                <img
                                    :src="
                                        getMediaLink(
                                            card.cover?.[0]?.url || '',
                                            1200
                                        ) ||
                                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                                    "
                                    :alt="card.name"
                                    class="h-full w-full object-cover" />
                            </picture>

                            <div
                                class="m-3 flex basis-[45%] flex-col lg:mx-10 lg:my-[4.75rem] lg:basis-[40%]">
                                <h2
                                    class="text-20/120 font-medium lg:text-32/100">
                                    {{ card.name }}
                                </h2>
                                <div
                                    v-html="card.description"
                                    class="mt-5 text-accent-100"></div>
                            </div>
                        </NuxtLink>
                    </div>

                    <div
                        v-else
                        class="no_content__blur relative mt-10 rounded-12 bg-white/5 p-5 pb-0 lg:p-20 xl:pb-30">
                        <p class="text-16/148 text-accent-100">
                            Пока тут ничего нет, но
                        </p>
                        <AtomTitle
                            class="mt-2 leading-8 lg:mt-6 lg:max-w-[32rem] lg:max-xl:!text-32/100 xl:max-w-3xl">
                            Скоро расскажем о&nbsp;том, для каких мероприятий
                            можно использовать оборудование Cromi
                        </AtomTitle>

                        <picture
                            class="mx-auto mt-10 block w-full max-w-[16.8rem] lg:absolute lg:bottom-0 lg:right-16 lg:max-w-[20rem] xl:max-w-[25.5rem]">
                            <source
                                type="webp"
                                srcset="
                                    /assets/images/ui/our-experience/webp/reinvox-t-duo.webp
                                " />

                            <img
                                alt=""
                                src="/assets/images/ui/our-experience/jpg-png/reinvox-t-duo.png"
                                class="h-auto w-full object-contain" />
                        </picture>
                    </div>
                </div>
            </LazyApiSection>
        </section>

        <template v-if="hasDeferredLoaded && solutions.length">
            <AtomHr />

            <RemainingQuestionsSection class="my-10 md:my-20" />
        </template>
    </main>
</template>

<script setup lang="ts">
import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import RemainingQuestionsSection from '~/components/particles/remaining-questions-section/RemainingQuestionsSection.vue'

const { data, error } = await useSolutionsPage(true, false)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const { data: deferredSolutionsData, execute: executeDeferredSolutions } =
    await useSolutionsPage(false, true, {
        key: 'deferred-solutions',
        server: false,
        watch: false,
    })

const isDeferredSolutionsContentLoading = ref(false)
const solutions = computed(
    () => deferredSolutionsData.value?.data.solutions?.data ?? []
)
const hasDeferredLoaded = computed(() => !!deferredSolutionsData.value)

async function loadDeferredSolutionsContent() {
    if (
        isDeferredSolutionsContentLoading.value ||
        deferredSolutionsData.value
    ) {
        return
    }

    isDeferredSolutionsContentLoading.value = true

    await executeDeferredSolutions()

    isDeferredSolutionsContentLoading.value = false
}

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: data.value?.data.name || 'Решения',
        to: `/solutions/`,
    },
])

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
}
</script>

<style scoped>
.no_content__blur {
    background-image: image-set(
        url('/assets/images/ui/blurs/our-experience-card/webp/standart-complect.webp')
            type('image/webp'),
        url('/assets/images/ui/blurs/our-experience-card/png/standart-complect.png')
            type('image/png')
    );
    background-position: center;
    background-repeat: no-repeat;
    background-size: 100% 100%;
}
</style>
